/** /p/<sessionId>?token=pt.… — the room screen: QR and code in the lobby, big bars and timer
 * live, correct highlighted on reveal. Aggregate only, never names. */
import { useParams, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { APP_BASE } from "../api/base";
import { deviceId } from "../live/device";
import { useJoinOrigin } from "../platform/joinOrigin";
import { useLive } from "../live/useLive";
import { product } from "../product";
import { DistributionBars, Qr, StatusBar, Timer } from "../ui";

export function Projector() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  // The teacher's "Open projector" link carries the projector token and the join code; the
  // projector token itself never reveals the code, so a leaked token cannot let people join.
  const { token, code } = useSearch({ strict: false }) as { token?: string; code?: string };
  const { t } = useTranslation();
  const dev = useMemo(() => `beamer-${deviceId()}`, []);
  const { snapshot, conn } = useLive(sessionId, token ?? "", dev);
  const joinCode = code ?? null;

  const item = snapshot?.item ?? null;
  const agg = snapshot?.aggregate ?? null;
  const open = snapshot?.state === "item_open";
  // The board must show an address a phone can reach: inside the Teacher Computer app this
  // window is served from 127.0.0.1, which is the teacher's own machine, not the room's.
  const origin = useJoinOrigin();
  const joinUrl = joinCode && origin ? `${origin}${APP_BASE}/j/${joinCode}` : "";

  return (
    <main className="board flex min-h-dvh flex-col p-10">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <span className="wordmark text-3xl" translate="no">{product.name}</span>
          {conn !== "live" && <StatusBar state={conn === "reconnecting" ? "reconnecting" : "offline"} />}
        </div>
        <div className="flex items-center gap-6">
          {joinCode && (
            <div className="text-right">
              <div className="text-3xl opacity-80">{t("projector.join")}</div>
              <div className="bignum text-6xl">{joinCode}</div>
            </div>
          )}
          {joinUrl && <Qr text={joinUrl} size={140} label={t("live.qrLabel")} />}
        </div>
      </header>
      <section className="flex flex-1 flex-col justify-center gap-10">
        {!item && (
          <div className="flex flex-col items-center gap-8">
            {joinUrl ? <div className="rounded-[var(--radius-sheet)] bg-white p-6"><Qr text={joinUrl} size={360} label={t("live.qrLabel")} /></div> : null}
            <p className="text-[40px]">{t("projector.waiting", { count: snapshot?.present ?? 0 })}</p>
          </div>
        )}
        {item && (
          <>
            <div className="flex items-start justify-between gap-10">
              <h1 className="min-w-0 flex-1 text-[64px] leading-tight font-medium break-words">{item.snapshot.spoken ? t("projector.listen") : item.snapshot.stem}</h1>
              <Timer deadline={open ? item.deadline : null} size="projector" />
            </div>
            {agg && item.snapshot.type === "short_text" && (agg.clusters ?? []).length > 0 && (
              <ul className="flex flex-col gap-3" aria-label={t("live.clusters.title", { n: agg.clusters?.length ?? 0 })}>
                {(agg.clusters ?? []).slice(0, 6).map((c) => (
                  <li key={c.cluster_id} className="flex items-center gap-6 text-[40px] leading-tight">
                    <span className="min-w-24 text-right tabular-nums opacity-80">{c.count}</span>
                    <span className="min-w-0 flex-1 truncate">{c.label}</span>
                    {c.verdict != null && <span aria-hidden>{c.verdict ? "✓" : "✗"}</span>}
                  </li>
                ))}
              </ul>
            )}
            {agg && item.snapshot.type !== "short_text" && (
              <DistributionBars
                bars={agg.options.map((o) => ({ key: o.key, label: o.key, count: o.count, correct: o.correct, before: agg.before[o.key] }))}
                total={Math.max(agg.answered, 1)}
                mode={open ? "live" : item.revealed === "correct" ? "revealed" : "closed"}
                size="projector"
                counts={false}
              />
            )}
            <p className="text-[40px] opacity-80 tabular-nums">{t("projector.answered", { a: agg?.answered ?? 0, p: snapshot?.present ?? 0 })}</p>
          </>
        )}
      </section>
    </main>
  );
}
